import { useMutation } from '@apollo/client';
import type { UpdateDocumentVersionMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  Document_File_Type_Enum,
  namedOperations,
  UpdateDocumentVersionDocument,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import type { UpdateDocumentVersionTRPCInput } from './useUpdateDocumentVersionTRPC';
import { useUpdateDocumentVersionTRPC } from './useUpdateDocumentVersionTRPC';

interface UpdateDocumentVersionGraphQLVariables {
  Id: string;
  LatestModifiedAtTimestamp: string;
  Status: UpdateDocumentVersionTRPCInput['Status'];
  Version: UpdateDocumentVersionTRPCInput['Version'];
  Type: Document_File_Type_Enum;
  FileId: string | null;
  Content: string | null;
  Link: string | null;
  Summary: UpdateDocumentVersionTRPCInput['Summary'];
  ReasonForReview: UpdateDocumentVersionTRPCInput['ReasonForReview'];
  ReviewedBy: UpdateDocumentVersionTRPCInput['ReviewedBy'];
  ReviewDate: UpdateDocumentVersionTRPCInput['ReviewDate'];
  NextReviewDate: UpdateDocumentVersionTRPCInput['NextReviewDate'];
  CustomAttributeData: UpdateDocumentVersionTRPCInput['CustomAttributeData'];
}

const toGraphQLVariables = (
  input: UpdateDocumentVersionTRPCInput
): UpdateDocumentVersionGraphQLVariables => {
  const shared = {
    Id: input.Id,
    LatestModifiedAtTimestamp: input.LatestModifiedAtTimestamp,
    Status: input.Status,
    Version: input.Version,
    Summary: input.Summary,
    ReasonForReview: input.ReasonForReview,
    ReviewedBy: input.ReviewedBy,
    ReviewDate: input.ReviewDate,
    NextReviewDate: input.NextReviewDate,
    CustomAttributeData: input.CustomAttributeData,
  };
  switch (input.Type) {
    case 'file':
      return {
        ...shared,
        Type: Document_File_Type_Enum.File,
        FileId: input.FileId,
        Content: null,
        Link: null,
      };
    case 'html':
      return {
        ...shared,
        Type: Document_File_Type_Enum.Html,
        FileId: null,
        Content: input.Content,
        Link: null,
      };
    case 'link':
      return {
        ...shared,
        Type: Document_File_Type_Enum.Link,
        FileId: null,
        Content: null,
        Link: input.Link,
      };
  }
};

export interface UpdateDocumentVersionOptions {
  /**
   * When set, threads the `x-confirm-change-request` and (optional)
   * `x-requester-comment` headers onto the GraphQL mutation context — the
   * legacy publish/CR flow relies on these. The tRPC path doesn't yet
   * support change-request headers; the options are a no-op there.
   */
  confirmChangeRequest?: boolean;
  requesterComment?: string;
}

export const useUpdateDocumentVersion = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const [updateGraphQL, graphqlState] = useMutation(
    UpdateDocumentVersionDocument,
    {
      update: (cache) => {
        evictField(cache, 'document');
        evictField(cache, 'document_file');
        evictField(cache, 'document_file_by_pk');
        evictField(cache, 'change_request');
      },
      refetchQueries: [namedOperations.Query.getDocumentFileById],
    }
  );

  const trpcMutation = useUpdateDocumentVersionTRPC();

  const updateDocumentVersion = async (
    input: UpdateDocumentVersionTRPCInput,
    options?: UpdateDocumentVersionOptions
  ): Promise<UpdateDocumentVersionMutation> => {
    if (trpcEnabled) {
      return trpcMutation.updateDocumentVersion(input);
    }

    const result = await updateGraphQL({
      variables: toGraphQLVariables(input),
      context: {
        headers: {
          'x-confirm-change-request': options?.confirmChangeRequest
            ? 'true'
            : 'false',
          ...(options?.requesterComment
            ? {
                'x-requester-comment': encodeURIComponent(
                  options.requesterComment
                ),
              }
            : {}),
        },
      },
    });
    if (!result.data) {
      throw new Error('Failed to update document version');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      updateDocumentVersion,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    updateDocumentVersion,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
